<template>
  <div class="min-h-screen bg-white">
    <!-- Side Navigation (Desktop Only) -->
    <SideNavbar currentRoute="explore" />

    <!-- Main Content Area with left margin for desktop -->
    <div class="md:ml-20">
    <!-- Notification Toast -->
    <transition
      enter-active-class="animate-[slideDown_0.3s_ease-out]"
      leave-active-class="animate-[slideUp_0.3s_ease-in]"
    >
      <div
        v-if="notification.show"
        class="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 px-4 py-2 rounded-lg shadow-lg text-white text-sm font-medium"
        :class="{
          'bg-teal-500': notification.type === 'info',
          'bg-green-500': notification.type === 'success',
          'bg-red-500': notification.type === 'error'
        }"
      >
        {{ notification.message }}
      </div>
    </transition>

    <!-- Header -->
    <div class="p-4 md:p-6 lg:p-8 bg-white md:max-w-6xl md:mx-auto">

      <!-- Main heading with hand emoji -->
      <h1 class="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
        Where do you like to go? <span class="text-xl md:text-2xl lg:text-3xl">👋</span>
      </h1>

      <!-- Search bar with autocomplete -->
      <div class="relative mb-6 md:max-w-md search-container">
        <div class="flex items-center bg-gray-100 rounded-full px-4 py-2">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search destinations"
            class="bg-transparent border-none w-full focus:outline-none ml-2 text-sm"
            @input="filterSuggestions"
            @keyup.enter="handleEnterKey"
            @keydown.down="navigateSuggestion(1)"
            @keydown.up="navigateSuggestion(-1)"
            @keydown.esc="showSuggestions = false"
            @focus="showSuggestions = true"
          />
          <button
            v-if="searchQuery"
            @click="searchDestinations"
            class="ml-2 bg-teal-500 text-white rounded-full p-1 hover:bg-teal-600 transition"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </button>
        </div>

        <!-- Suggestions dropdown -->
        <div
          v-if="showSuggestions && filteredSuggestions.length > 0"
          class="absolute z-10 mt-1 w-full bg-white rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          <ul class="py-1">
            <li
              v-for="(suggestion, index) in filteredSuggestions"
              :key="index"
              @click="selectSuggestion(suggestion)"
              @mouseenter="highlightedIndex = index"
              :class="[
                'px-4 py-2 cursor-pointer text-sm',
                highlightedIndex === index ? 'bg-teal-100' : 'hover:bg-gray-100'
              ]"
            >
              {{ suggestion }}
            </li>
          </ul>
        </div>
      </div>

      <!-- Category buttons -->
      <div class="flex space-x-3 mb-6 overflow-x-auto pb-2 hide-scrollbar touch-scroll">
        <button
          v-for="category in categories"
          :key="category"
          @click="selectCategory(category)"
          :class="[
            'px-4 py-2 rounded-full text-sm font-medium flex-shrink-0 transition-colors whitespace-nowrap',
            selectedCategory === category
              ? 'bg-teal-500 text-white'
              : 'bg-teal-100 text-teal-800 hover:bg-teal-200'
          ]"
        >
          {{ category }}
        </button>
      </div>
    </div>

    <!-- Loading Indicator -->
    <div v-if="isSearching" class="px-4 mb-6 md:max-w-6xl md:mx-auto flex justify-center items-center py-8">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
    </div>

    <!-- Search Results Section -->
    <div v-else-if="searchResults.length > 0" class="px-3 sm:px-4 lg:px-6 mb-6 md:max-w-7xl md:mx-auto">
      <h2 class="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-6 md:mb-8 text-left lg:text-left">
        {{ selectedCategory === 'All' ? 'Search Results' : selectedCategory + ' Destinations' }}
      </h2>
      <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        <div
          v-for="(result, index) in searchResults"
          :key="index"
          class="destination-card bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl relative cursor-pointer group"
          @click="showDestinationDetails(result)"
        >
          <!-- Image Container -->
          <div class="relative h-32 sm:h-36 md:h-40 lg:h-44 xl:h-48 overflow-hidden">
            <img
              :src="result.image || result.images?.[0] || getDefaultImage(result.type || result.category)"
              :alt="result.name"
              class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              @error="$event.target.src = getDefaultImage(result.type || result.category)"
            />
            <!-- Gradient Overlay -->
            <div class="absolute inset-0"></div>

            <!-- Category Badge -->
            <div v-if="result.type || result.category" class="absolute top-2 right-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white text-xs font-medium px-2.5 py-1 rounded-full shadow-lg backdrop-blur-sm">
              {{ result.type || result.category }}
            </div>

            <!-- Favorite Button -->
            <button class="absolute top-2 left-2 p-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white/30 active:scale-95">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </button>
          </div>

          <!-- Content Container -->
          <div class="p-3 sm:p-4">
            <div class="space-y-1">
              <!-- Destination Name -->
              <h3 class="font-bold text-gray-900 text-sm sm:text-base lg:text-lg leading-tight line-clamp-2 group-hover:text-teal-600 transition-colors duration-200">
                {{ result.name || 'Unnamed Location' }}
              </h3>

              <!-- Location -->
              <div class="flex items-center text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 sm:h-4 sm:w-4 mr-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                </svg>
                <p class="text-xs sm:text-sm font-medium truncate">
                  {{ result.city || result.location || 'India' }}{{ result.state ? ', ' + result.state : '' }}
                </p>
              </div>
            </div>
          </div>

          <!-- Hover Effect Overlay -->
          <div class="absolute inset-0 bg-gradient-to-t from-teal-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
        </div>
      </div>
    </div>

    <!-- All Destinations from Home.json -->
    <div v-if="!isSearching && !isLoadingDestinations && homeDestinations.length > 0" class="px-3 sm:px-4 lg:px-6 mb-6 md:max-w-7xl md:mx-auto">
      <h2 class="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-6 md:mb-8 text-left lg:text-left">Famous Destinations</h2>
      <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        <div
          v-for="(destination, index) in homeDestinations"
          :key="destination.name || index"
          class="destination-card bg-white rounded-2xl overflow-hidden hover:shadow-xl relative cursor-pointer group"
          @click="showDestinationDetails(destination)"
        >
          <!-- Image Container -->
          <div class="relative h-32 sm:h-36 md:h-40 lg:h-44 xl:h-48 overflow-hidden">
            <img
              :src="destination.images && destination.images.length > 0 ? destination.images[0] : getDefaultImage(destination.category)"
              :alt="destination.name"
              class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              @error="$event.target.src = getDefaultImage(destination.category)"
            />
            <!-- Gradient Overlay -->
            <div class="absolute inset-0"></div>

            <!-- Category Badge -->
            <div v-if="destination.category" class="absolute top-2 right-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white text-xs font-medium px-2.5 py-1 rounded-full shadow-lg backdrop-blur-sm">
              {{ destination.category }}
            </div>

            <!-- Favorite Button -->
            <button class="absolute top-2 left-2 p-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white/30 active:scale-95">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </button>
          </div>

          <!-- Content Container -->
          <div class="p-3 sm:p-4">
            <div class="space-y-1">
              <!-- Destination Name -->
              <h3 class="font-bold text-gray-900 text-sm sm:text-base lg:text-lg leading-tight line-clamp-2 group-hover:text-teal-600 transition-colors duration-200">
                {{ destination.name }}
              </h3>

              <!-- Location -->
              <div class="flex items-center text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 sm:h-4 sm:w-4 mr-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                </svg>
                <p class="text-xs sm:text-sm font-medium truncate">{{ destination.city }}, {{ destination.state }}</p>
              </div>
            </div>
          </div>

          <!-- Hover Effect Overlay -->
          <div class="absolute inset-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoadingDestinations && !isSearching" class="px-4 mb-6 md:max-w-6xl md:mx-auto">
      <div class="flex items-center justify-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
        <span class="ml-3 text-gray-600">Loading destinations...</span>
      </div>
    </div>

    <!-- Error State -->
    <div v-if="destinationsError && !isSearching" class="px-4 mb-6 md:max-w-6xl md:mx-auto">
      <div class="bg-red-50 border border-red-200 rounded-lg p-4">
        <div class="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-red-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span class="text-red-700">{{ destinationsError }}</span>
        </div>
      </div>
    </div>

    <!-- Destination Details Modal - Responsive Design -->
    <div v-if="selectedDestination"
         class="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm overflow-y-auto"
         :class="{'animate-fadeIn': selectedDestination}"
         @click.self="closeDestinationDetails">

      <!-- Mobile Layout (fullscreen) -->
      <div class="md:hidden bg-white min-h-full relative">
        <div class="flex flex-col h-screen">
          <!-- Hero Image Section (Fixed) -->
          <div class="relative w-full h-[50vh] bg-white flex-shrink-0">
            <!-- Mobile Arrow Navigation (replacing touch swipe) -->
            <div v-if="selectedDestination.images && selectedDestination.images.length > 1" class="absolute inset-x-0 top-1/2 transform -translate-y-1/2 flex justify-between items-center px-4 z-10 md:hidden">
              <button
                @click="prevImage"
                class="bg-black/30 backdrop-blur-sm rounded-full p-2 text-white transform transition-transform active:scale-95 hover:bg-black/50"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                @click="nextImage"
                class="bg-black/30 backdrop-blur-sm rounded-full p-2 text-white transform transition-transform active:scale-95 hover:bg-black/50"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            <!-- Single Image Display -->
            <img
              :src="selectedDestination.images && selectedDestination.images.length > 0 ? selectedDestination.images[currentImageIndex] : getDefaultImage(selectedDestination.type)"
              :alt="selectedDestination.name"
              class="w-full h-full object-cover transition-opacity duration-300"
              :class="{'opacity-100': !imageLoading, 'opacity-0': imageLoading}"
              @load="imageLoading = false"
            >

            <!-- Back Button -->
            <div class="absolute top-safe-top left-4 z-10">
              <button
                @click="closeDestinationDetails"
                class="rounded-full p-3 transform transition-transform active:scale-95"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            </div>

            <!-- Favorite Button -->
            <div class="absolute top-safe-top right-4 z-10">
              <button
                class="rounded-full p-3 transform transition-transform active:scale-95"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </button>
            </div>

            <!-- Image Navigation Controls - Only visible on desktop/larger screens -->
            <div class="absolute inset-x-0 top-1/2 transform -translate-y-1/2 hidden md:flex justify-between items-center px-4 z-10">
              <button
                v-if="selectedDestination.images && selectedDestination.images.length > 1"
                @click="prevImage"
                class="bg-black/30 backdrop-blur-sm rounded-full p-2 text-white transform transition-transform active:scale-95 hover:bg-black/50"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                v-if="selectedDestination.images && selectedDestination.images.length > 1"
                @click="nextImage"
                class="bg-black/30 backdrop-blur-sm rounded-full p-2 text-white transform transition-transform active:scale-95 hover:bg-black/50"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            <!-- Navigation Dots -->
            <div v-if="selectedDestination.images && selectedDestination.images.length > 1" class="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-1.5 z-10">
              <button
                v-for="(_, index) in selectedDestination.images"
                :key="index"
                @click="currentImageIndex = index"
                :class="[
                  'w-1 h-1 rounded-full transition-all duration-300 transform',
                  currentImageIndex === index ? 'bg-white scale-125 w-2' : 'bg-white/50 hover:bg-white/80'
                ]"
              ></button>
            </div>

            <!-- Title Overlay on Image -->
            <div class="absolute bottom-8 left-0 right-0 p-4 md:p-6 text-white z-10">
              <h1 class="text-3xl md:text-4xl font-bold text-shadow-lg mb-1">{{ selectedDestination.name }}</h1>
              <div class="flex items-center text-sm md:text-base">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                </svg>
                <span>{{ selectedDestination.city }}, {{ selectedDestination.state }}</span>
              </div>
            </div>
          </div>

          <!-- Content Section (Scrollable) -->
          <div class="flex-1 flex flex-col overflow-hidden">
            <!-- Mobile Tabs Navigation with slide-up effect -->
            <div
              class="sticky top-0 bg-white z-30 px-4 pt-4 pb-0 shadow-sm transition-transform duration-300 ease-out"
              :class="{ 'slide-up-over-image': isContentSlideUp }"
            >
              <!-- Category Badge -->
              <div class="mb-3">
                <div v-if="selectedDestination.type" class="bg-green-100 text-green-800 rounded-full px-3 py-2 text-sm font-medium inline-block">
                  {{ selectedDestination.type }}
                </div>
              </div>

              <!-- Tabs and Share Icon on Same Line -->
              <div class="flex justify-between items-center border-b border-gray-200">
                <!-- Tabs -->
                <div class="flex space-x-8">
                  <button @click="switchTab('about')" :class="['py-3 text-base font-medium border-b-2 transition-colors duration-300 relative', activeTab === 'about' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700']">About</button>
                  <button @click="switchTab('gallery')" :class="['py-3 text-base font-medium border-b-2 transition-colors duration-300 relative', activeTab === 'gallery' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700']">Gallery</button>
                  <button @click="switchTab('related')" :class="['py-3 text-base font-medium border-b-2 transition-colors duration-300 relative', activeTab === 'related' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700']">Related</button>
                </div>

                <!-- Share Button in Corner -->
                <button class="text-gray-500 p-2 rounded-full hover:bg-gray-100 transition-colors ml-4">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Scrollable Content Area -->
            <div class="flex-1 overflow-y-auto modal-scrollable-content">
            <!-- Mobile Tab Content -->
            <div class="p-4">
              <!-- About Tab -->
              <div v-if="activeTab === 'about'" class="animate-fadeIn">
                <div class="mb-6">
                  <h3 class="text-lg font-semibold text-gray-800 mb-2">About</h3>
                  <p class="text-gray-600 text-base leading-relaxed">{{ selectedDestination.description }}</p>
                </div>
                <div v-if="selectedDestination.what_to_expect" class="mb-6">
                  <h3 class="text-lg font-semibold text-gray-800 mb-2">What to Expect</h3>
                  <p class="text-gray-600 text-base leading-relaxed">{{ selectedDestination.what_to_expect }}</p>
                </div>
                <div v-if="selectedDestination.peak_season || selectedDestination.offseason" class="mb-6">
                  <h3 class="text-lg font-semibold text-gray-800 mb-2">Best Time to Visit</h3>
                  <div class="space-y-3">
                    <div v-if="selectedDestination.peak_season" class="flex items-start">
                      <div class="bg-green-100 text-green-800 rounded-full px-3 py-1 text-sm font-medium mr-3 mt-0.5">Peak Season</div>
                      <p class="text-gray-600 flex-1">{{ selectedDestination.peak_season }}</p>
                    </div>
                    <div v-if="selectedDestination.offseason" class="flex items-start">
                      <div class="bg-yellow-100 text-yellow-800 rounded-full px-3 py-1 text-sm font-medium mr-3 mt-0.5">Off Season</div>
                      <p class="text-gray-600 flex-1">{{ selectedDestination.offseason }}</p>
                    </div>
                  </div>
                </div>
                <div v-if="selectedDestination.things_you_ll_love && selectedDestination.things_you_ll_love.length > 0" class="mb-6">
                  <h3 class="text-lg font-semibold text-gray-800 mb-2">Things You'll Love</h3>
                  <ul class="space-y-2">
                    <li v-for="(item, index) in selectedDestination.things_you_ll_love" :key="index" class="flex items-start">
                      <div class="bg-green-100 rounded-full p-1 mr-3 mt-0.5">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <p class="text-gray-600 flex-1">{{ item }}</p>
                    </li>
                  </ul>
                </div>
              </div>
              <!-- Gallery Tab -->
              <div v-if="activeTab === 'gallery'" class="animate-fadeIn">
                <div class="grid grid-cols-2 gap-3">
                  <div v-for="(image, index) in selectedDestination.images || []" :key="index" class="aspect-square rounded-xl overflow-hidden cursor-pointer transform transition-transform hover:scale-[1.02] active:scale-[0.98]" @click="currentImageIndex = index; scrollToTop()">
                    <img :src="image" :alt="`${selectedDestination.name} image ${index + 1}`" class="w-full h-full object-cover">
                  </div>
                </div>
              </div>
              <!-- Related Tab -->
              <div v-if="activeTab === 'related'" class="animate-fadeIn">
                <!-- Loading State -->
                <div v-if="isLoadingRelated" class="flex flex-col items-center justify-center py-8">
                  <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mb-3"></div>
                  <p class="text-gray-500">Finding related destinations...</p>
                </div>

                <!-- Results -->
                <div v-else-if="relatedDestinations.length > 0" class="grid grid-cols-1 gap-4">
                  <div v-for="(result, index) in relatedDestinations" :key="index" @click="showDestinationDetails(result)" class="bg-white rounded-xl overflow-hidden shadow-md cursor-pointer transform transition-transform hover:scale-[1.02] active:scale-[0.98]">
                    <div class="h-40 relative">
                      <img :src="result.image || result.images?.[0] || getDefaultImage(result.type || result.category)" :alt="result.name" class="w-full h-full object-cover">
                      <div class="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                      <div class="absolute bottom-0 left-0 right-0 p-3 text-white">
                        <h3 class="font-bold text-lg">{{ result.name }}</h3>
                        <p class="text-sm text-white/90">{{ result.city || result.location }}, {{ result.state }}</p>
                      </div>
                      <!-- Category Badge -->
                      <div v-if="result.type || result.category" class="absolute top-2 right-2 bg-teal-600 text-white text-xs px-2 py-1 rounded-full">
                        {{ result.type || result.category }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Empty State -->
                <div v-else class="flex flex-col items-center justify-center py-8">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <p class="text-gray-500 text-center">No related destinations found for this category</p>
                </div>
              </div>
            </div>
            </div>

            <!-- Fixed Explore Now Button -->
            <div class="bg-white border-t border-gray-200 p-4 shadow-lg">
              <button
                @click="exploreNow"
                class="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-medium py-3 px-4 rounded-xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg"
              >
                Explore Now
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Desktop Layout (centered modal) -->
      <div class="hidden md:flex items-center justify-center min-h-screen p-4">
        <div class="bg-white rounded-3xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden relative">
          <!-- Close Button -->
          <button @click="closeDestinationDetails" class="absolute top-4 right-4 z-50 p-2 rounded-full bg-white/90 backdrop-blur-sm text-gray-600 hover:text-gray-800 hover:bg-white transition-all duration-200 shadow-lg">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div class="flex h-[80vh]">
            <!-- Left Panel - Hero Image Gallery (60%) -->
            <div class="w-3/5 relative bg-gray-100">
              <!-- Main Image -->
              <div class="relative h-full">
                <img :src="selectedDestination.images && selectedDestination.images.length > 0 ? selectedDestination.images[currentImageIndex] : getDefaultImage(selectedDestination.type)" :alt="selectedDestination.name" class="w-full h-full object-cover transition-opacity duration-300" :class="{'opacity-100': !imageLoading, 'opacity-0': imageLoading}" @load="imageLoading = false">

                <!-- Image Navigation -->
                <div v-if="selectedDestination.images && selectedDestination.images.length > 1" class="absolute inset-x-0 top-1/2 transform -translate-y-1/2 flex justify-between items-center px-4 z-10">
                  <button @click="prevImage" class="p-3 rounded-full bg-white/80 backdrop-blur-sm text-gray-800 hover:bg-white transition-all duration-200 shadow-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button @click="nextImage" class="p-3 rounded-full bg-white/80 backdrop-blur-sm text-gray-800 hover:bg-white transition-all duration-200 shadow-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                <!-- Image Dots -->
                <div v-if="selectedDestination.images && selectedDestination.images.length > 1" class="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
                  <button v-for="(_, index) in selectedDestination.images" :key="index" @click="currentImageIndex = index" :class="['w-2 h-1 rounded-full transition-all duration-300', currentImageIndex === index ? 'bg-white scale-125' : 'bg-white/50 hover:bg-white/80']"></button>
                </div>

                <!-- Image Counter -->
                <div v-if="selectedDestination.images && selectedDestination.images.length > 1" class="absolute top-4 left-4 bg-black/50 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-medium">
                  {{ currentImageIndex + 1 }} / {{ selectedDestination.images.length }}
                </div>

                <!-- Title Overlay -->
                <div class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6 text-white">
                  <h1 class="text-3xl font-bold mb-2">{{ selectedDestination.name }}</h1>
                  <div class="flex items-center text-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    </svg>
                    <span>{{ selectedDestination.city }}, {{ selectedDestination.state }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Panel - Content (40%) -->
            <div class="w-2/5 flex flex-col">
              <!-- Header -->
              <div class="p-6 border-b border-gray-200">
                <div class="flex items-center justify-between mb-4">
                  <div v-if="selectedDestination.type" class="bg-green-100 text-green-800 rounded-full px-4 py-2 text-sm font-medium">
                    {{ selectedDestination.type }}
                  </div>
                  <button class="text-gray-500 p-2 rounded-full hover:bg-gray-100 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                  </button>
                </div>

                <!-- Tabs -->
                <div class="flex space-x-6 border-b border-gray-200">
                  <button @click="switchTab('about')" :class="['py-3 text-sm font-medium border-b-2 transition-colors duration-300', activeTab === 'about' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700']">About</button>
                  <button @click="switchTab('gallery')" :class="['py-3 text-sm font-medium border-b-2 transition-colors duration-300', activeTab === 'gallery' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700']">Gallery</button>
                  <button @click="switchTab('related')" :class="['py-3 text-sm font-medium border-b-2 transition-colors duration-300', activeTab === 'related' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700']">Related</button>
                </div>
              </div>

              <!-- Scrollable Content -->
              <div class="flex-1 overflow-y-auto p-6">
                <!-- About Tab -->
                <div v-if="activeTab === 'about'" class="space-y-6">
                  <div>
                    <h3 class="text-lg font-semibold text-gray-800 mb-3">About</h3>
                    <p class="text-gray-600 leading-relaxed">{{ selectedDestination.description }}</p>
                  </div>
                  <div v-if="selectedDestination.what_to_expect">
                    <h3 class="text-lg font-semibold text-gray-800 mb-3">What to Expect</h3>
                    <p class="text-gray-600 leading-relaxed">{{ selectedDestination.what_to_expect }}</p>
                  </div>
                  <div v-if="selectedDestination.peak_season || selectedDestination.offseason">
                    <h3 class="text-lg font-semibold text-gray-800 mb-3">Best Time to Visit</h3>
                    <div class="space-y-3">
                      <div v-if="selectedDestination.peak_season" class="flex items-start">
                        <div class="bg-green-100 text-green-800 rounded-full px-3 py-1 text-sm font-medium mr-3 mt-0.5">Peak Season</div>
                        <p class="text-gray-600 flex-1">{{ selectedDestination.peak_season }}</p>
                      </div>
                      <div v-if="selectedDestination.offseason" class="flex items-start">
                        <div class="bg-yellow-100 text-yellow-800 rounded-full px-3 py-1 text-sm font-medium mr-3 mt-0.5">Off Season</div>
                        <p class="text-gray-600 flex-1">{{ selectedDestination.offseason }}</p>
                      </div>
                    </div>
                  </div>
                  <div v-if="selectedDestination.things_you_ll_love && selectedDestination.things_you_ll_love.length > 0">
                    <h3 class="text-lg font-semibold text-gray-800 mb-3">Things You'll Love</h3>
                    <ul class="space-y-2">
                      <li v-for="(item, index) in selectedDestination.things_you_ll_love" :key="index" class="flex items-start">
                        <div class="bg-green-100 rounded-full p-1 mr-3 mt-0.5">
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <p class="text-gray-600 flex-1">{{ item }}</p>
                      </li>
                    </ul>
                  </div>
                </div>

                <!-- Gallery Tab -->
                <div v-if="activeTab === 'gallery'" class="grid grid-cols-2 gap-3">
                  <div v-for="(image, index) in selectedDestination.images || []" :key="index" class="aspect-square rounded-lg overflow-hidden cursor-pointer transform transition-transform hover:scale-[1.02]" @click="currentImageIndex = index">
                    <img :src="image" :alt="`${selectedDestination.name} image ${index + 1}`" class="w-full h-full object-cover">
                  </div>
                </div>

                <!-- Related Tab -->
                <div v-if="activeTab === 'related'">
                  <!-- Loading State -->
                  <div v-if="isLoadingRelated" class="flex flex-col items-center justify-center py-12">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mb-3"></div>
                    <p class="text-gray-500">Finding related destinations...</p>
                  </div>

                  <!-- Results -->
                  <div v-else-if="relatedDestinations.length > 0" class="space-y-4">
                    <div v-for="(result, index) in relatedDestinations" :key="index" @click="showDestinationDetails(result)" class="bg-gray-50 rounded-lg overflow-hidden cursor-pointer transform transition-transform hover:scale-[1.02] hover:shadow-md">
                      <div class="h-32 relative">
                        <img :src="result.image || result.images?.[0] || getDefaultImage(result.type || result.category)" :alt="result.name" class="w-full h-full object-cover">
                        <div class="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                        <div class="absolute bottom-0 left-0 right-0 p-3 text-white">
                          <h3 class="font-bold">{{ result.name }}</h3>
                          <p class="text-sm text-white/90">{{ result.city || result.location }}, {{ result.state }}</p>
                        </div>
                        <!-- Category Badge -->
                        <div v-if="result.type || result.category" class="absolute top-2 right-2 bg-teal-600 text-white text-xs px-2 py-1 rounded-full">
                          {{ result.type || result.category }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Empty State -->
                  <div v-else class="flex flex-col items-center justify-center py-12">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <p class="text-gray-500 text-center">No related destinations found for this category</p>
                  </div>
                </div>
              </div>

              <!-- Footer with Action Button -->
              <div class="p-6 border-t border-gray-200">
                <button @click="exploreNow" class="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-medium py-3 px-4 rounded-xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg">
                  Explore Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Reusable Navbar component - Hidden on mobile when destination details modal is open -->
    <Navbar
      currentRoute="explore"
      :class="{ 'md:block': true, 'hidden': selectedDestination }"
    />
    </div>
  </div>
</template>

<script>
import Navbar from '@/components/Navbar.vue'
import SideNavbar from '@/components/SideNavbar.vue'
import { searchTravelData } from '@/services/searchService'
import {
  fetchHomeDestinations
} from '@/services/homeService'
import { searchSuggestions } from '@/utils/suggestions'
import { getCityFromCache } from '@/utils/locationUtils'

export default {
  name: 'ExploreView',
  components: {
    Navbar,
    SideNavbar
  },
  async mounted() {
    // Initialize notification system
    this.hideNotificationTimer = null;

    // Add click outside listener for suggestions dropdown with passive option for better performance
    document.addEventListener('click', this.handleClickOutside, { passive: true });

    // Load current location from cached weather data
    this.loadCurrentLocationFromCache();

    // Load home destinations data
    await this.loadHomeDestinations();
  },
  beforeUnmount() {
    // Clean up any pending notification timers
    if (this.hideNotificationTimer) {
      clearTimeout(this.hideNotificationTimer);
    }

    // Remove click outside listener with same options as when added
    document.removeEventListener('click', this.handleClickOutside, { passive: true });
  },
  data() {
    return {
      searchQuery: '',
      searchResults: [],
      isSearching: false,
      selectedCategory: 'All',
      categories: ['All', 'Adventure', 'Beach', 'Cultural', 'Entertainment', 'Historical', 'Natural', 'Wildlife'],
      selectedDestination: null,
      currentImageIndex: 0,
      activeTab: 'about',
      imageLoading: true,
      touchStartX: 0,
      touchMoveX: 0,
      touchOffset: 0,
      isDragging: false,
      animationFrame: null,
      // Mobile modal scroll behavior
      modalScrollY: 0,
      isContentSlideUp: false,
      relatedDestinations: [],
      isLoadingRelated: false,
      showSuggestions: false,
      highlightedIndex: -1,
      filteredSuggestions: [],
      // Home destinations data
      homeDestinations: [],
      isLoadingDestinations: true,
      destinationsError: null,
      suggestions: searchSuggestions,
      // Current location data (just city name, no country code)
      currentLocationName: 'Gokarna',
      notification: {
        show: false,
        message: '',
        type: 'info' // 'info', 'success', 'error'
      },
      defaultImages: {
        'beach': 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        'mountain': 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        'temple': 'https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        'fort': 'https://images.unsplash.com/photo-1599930113854-d6d7fd522194?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        'park': 'https://images.unsplash.com/photo-1519331379826-f10be5486c6f?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        'lake': 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        'waterfall': 'https://images.unsplash.com/photo-1546484396-fb3fc6f95f98?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        'historical': 'https://images.unsplash.com/photo-1563889958749-625da26ed355?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
        'default': 'https://images.unsplash.com/photo-1500835556837-99ac94a94552?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80'
      }
    }
  },
  computed: {
    currentLocationDisplay() {
      return this.currentLocationName
    }
  },
  methods: {
    // Load current location from cached weather data
    loadCurrentLocationFromCache() {
      try {
        const cachedWeatherData = localStorage.getItem('travley_user_weather_cache');
        if (cachedWeatherData) {
          const data = JSON.parse(cachedWeatherData);

          // Use utility function to get clean city name (no country code, no diacritics)
          this.currentLocationName = getCityFromCache(data);
        }
      } catch (error) {
        // If there's an error reading cached data, keep default location
        console.warn('Failed to load location from cache:', error);
      }
    },

    // Load home destinations from the deployed JSON file with localStorage caching (24-hour TTL)
    async loadHomeDestinations() {
      try {
        this.isLoadingDestinations = true;
        this.destinationsError = null;

        // Fetch destinations from home.json (uses cache if available and valid)
        const destinations = await fetchHomeDestinations();
        this.homeDestinations = destinations;

        // Keep only hardcoded suggestions - do not add destination names from home.json
        // The suggestions array already contains the hardcoded suggestions defined in data()
      } catch (error) {
        this.destinationsError = error.message;
        this.showNotification('Failed to load destinations. Please refresh the page.', 'error');
      } finally {
        this.isLoadingDestinations = false;
      }
    },

    // Filter suggestions based on user input
    filterSuggestions() {
      if (!this.searchQuery || this.searchQuery.trim().length < 1) {
        this.filteredSuggestions = [];
        return;
      }

      const query = this.searchQuery.toLowerCase().trim();
      this.filteredSuggestions = this.suggestions.filter(suggestion =>
        suggestion.toLowerCase().includes(query)
      ).slice(0, 10); // Limit to 10 suggestions for better UX

      this.highlightedIndex = this.filteredSuggestions.length > 0 ? 0 : -1;
    },

    // Navigate through suggestions with keyboard
    navigateSuggestion(direction) {
      if (this.filteredSuggestions.length === 0) return;

      const newIndex = this.highlightedIndex + direction;
      if (newIndex >= 0 && newIndex < this.filteredSuggestions.length) {
        this.highlightedIndex = newIndex;
      }
    },

    // Select a suggestion from the dropdown
    selectSuggestion(suggestion) {
      this.searchQuery = suggestion;
      this.showSuggestions = false;
      this.searchDestinations();
    },

    // Handle Enter key press in the search input
    handleEnterKey() {
      // If suggestions are shown and an item is highlighted, select it
      if (this.showSuggestions && this.highlightedIndex >= 0 && this.filteredSuggestions.length > 0) {
        this.selectSuggestion(this.filteredSuggestions[this.highlightedIndex]);
      } else {
        // Otherwise, perform search with current query
        this.searchDestinations();
      }
    },

    // Find the closest match in suggestions
    findClosestMatch(query) {
      if (!query || query.trim() === '') return null;

      const normalizedQuery = query.toLowerCase().trim();

      // First try exact match
      const exactMatch = this.suggestions.find(s =>
        s.toLowerCase() === normalizedQuery
      );
      if (exactMatch) return exactMatch;

      // Then try contains match
      const containsMatches = this.suggestions.filter(s =>
        s.toLowerCase().includes(normalizedQuery)
      );
      if (containsMatches.length > 0) return containsMatches[0];

      // Finally try to find the closest match using Levenshtein distance
      let closestMatch = null;
      let minDistance = Infinity;

      for (const suggestion of this.suggestions) {
        const distance = this.levenshteinDistance(normalizedQuery, suggestion.toLowerCase());
        if (distance < minDistance) {
          minDistance = distance;
          closestMatch = suggestion;
        }
      }

      // Only return if it's reasonably close (threshold of 3)
      return minDistance <= 3 ? closestMatch : null;
    },

    // Calculate Levenshtein distance between two strings
    levenshteinDistance(a, b) {
      if (a.length === 0) return b.length;
      if (b.length === 0) return a.length;

      const matrix = [];

      // Initialize matrix
      for (let i = 0; i <= b.length; i++) {
        matrix[i] = [i];
      }

      for (let j = 0; j <= a.length; j++) {
        matrix[0][j] = j;
      }

      // Fill matrix
      for (let i = 1; i <= b.length; i++) {
        for (let j = 1; j <= a.length; j++) {
          const cost = a[j - 1] === b[i - 1] ? 0 : 1;
          matrix[i][j] = Math.min(
            matrix[i - 1][j] + 1,      // deletion
            matrix[i][j - 1] + 1,      // insertion
            matrix[i - 1][j - 1] + cost // substitution
          );
        }
      }

      return matrix[b.length][a.length];
    },

    async searchDestinations() {
      // Hide suggestions
      this.showSuggestions = false;

      // Find closest match from suggestions
      const closestMatch = this.findClosestMatch(this.searchQuery);

      if (closestMatch) {
        // Use the closest match for searching
        this.searchQuery = closestMatch;
      } else {
        // No close match found
        this.showNotification('Please select from the suggestions list', 'info');
        return;
      }

      this.isSearching = true;
      this.searchResults = [];

      try {
        // Get the type based on selected category
        const type = this.selectedCategory === 'All' ? null : this.selectedCategory;

        // Call the API to search for travel data
        const response = await searchTravelData(this.searchQuery, type);

        if (response && response.results) {
          this.searchResults = response.results;

          // If no results found, show a message
          if (this.searchResults.length === 0) {
            this.showNotification(`No results found for "${this.searchQuery}"`, 'info');
          }
        } else {
          this.showNotification('No results found', 'info');
        }
      } catch (error) {
        this.showNotification('Something went wrong. Please try again.', 'error');
      } finally {
        this.isSearching = false;
      }
    },

    getDefaultImage(type) {
      if (!type) return this.defaultImages.default;

      // Convert type to lowercase for case-insensitive matching
      const lowerType = type.toLowerCase();

      // Check if we have a default image for this type
      for (const [key, url] of Object.entries(this.defaultImages)) {
        if (lowerType.includes(key)) {
          return url;
        }
      }

      // Return default image if no match found
      return this.defaultImages.default;
    },

    selectCategory(category) {
      this.selectedCategory = category;

      // Don't search if "All" is selected, just reset the view
      if (category === 'All') {
        this.searchResults = [];
        return;
      }

      // Show loading indicator
      this.isSearching = true;

      // Call the tour worker with the selected category
      this.searchTravelByCategory(category);
    },

    async searchTravelByCategory(category) {
      try {
        // Call the searchTravelData function with empty query and the category as type
        const response = await searchTravelData('', category);

        if (response && response.results && response.results.length > 0) {
          this.searchResults = response.results;
        } else {
          this.searchResults = [];
          this.showNotification(`No results found for ${category}`, 'info');
        }
      } catch (error) {
        this.searchResults = [];
        this.showNotification('Something went wrong. Please try again.', 'error');
      } finally {
        this.isSearching = false;
      }
    },

    showNotification(message, type = 'info') {
      // If a notification is already showing, hide it first
      if (this.notification.show) {
        this.notification.show = false;

        // Wait for the hide animation to complete before showing the new notification
        setTimeout(() => {
          this.notification = {
            show: true,
            message,
            type
          };

          // Auto-hide notification after 3 seconds
          this.hideNotificationTimer = setTimeout(() => {
            this.notification.show = false;
          }, 3000);
        }, 300); // Match the duration of the slideUp animation
      } else {
        this.notification = {
          show: true,
          message,
          type
        };

        // Auto-hide notification after 3 seconds
        this.hideNotificationTimer = setTimeout(() => {
          this.notification.show = false;
        }, 3000);
      }
    },

    showDestinationDetails(destination) {
      this.imageLoading = true;
      this.selectedDestination = destination;
      this.currentImageIndex = 0;
      this.activeTab = 'about';
      this.relatedDestinations = [];
      this.isLoadingRelated = false;

      // Reset mobile scroll state
      this.modalScrollY = 0;
      this.isContentSlideUp = false;

      // Prevent scrolling on the body when modal is open
      document.body.style.overflow = 'hidden';

      // Add scroll listener for mobile slide-up effect
      this.$nextTick(() => {
        const scrollableContent = document.querySelector('.modal-scrollable-content');
        if (scrollableContent) {
          scrollableContent.addEventListener('scroll', this.handleModalScroll, { passive: true });
        }
      });
    },

    // Handle tab switching and trigger related search when needed
    switchTab(tabName) {
      this.activeTab = tabName;

      // If switching to related tab, search for related destinations
      if (tabName === 'related' && this.selectedDestination && this.relatedDestinations.length === 0) {
        this.searchRelatedDestinations();
      }
    },

    // Search for related destinations based on current destination's category
    async searchRelatedDestinations() {
      if (!this.selectedDestination) return;

      const category = this.selectedDestination.category || this.selectedDestination.type;
      if (!category) {
        this.relatedDestinations = [];
        return;
      }

      this.isLoadingRelated = true;
      this.relatedDestinations = [];

      try {
        // Use the same search mechanism as category buttons
        const response = await searchTravelData('', category);

        if (response && response.results && response.results.length > 0) {
          // Filter out the current destination from results
          this.relatedDestinations = response.results.filter(result =>
            result.name !== this.selectedDestination.name
          ).slice(0, 10); // Limit to 10 results
        } else {
          this.relatedDestinations = [];
        }
      } catch (error) {
        this.relatedDestinations = [];
        this.showNotification('Failed to load related destinations', 'error');
      } finally {
        this.isLoadingRelated = false;
      }
    },

    // Navigate to maps page and show destination location
    exploreNow() {
      if (!this.selectedDestination) {
        this.showNotification('No destination selected', 'error');
        return;
      }

      // Store destination data before closing modal
      const destination = { ...this.selectedDestination };

      // Function to validate coordinates
      const isValidCoordinate = (lat, lon) => {
        const latitude = parseFloat(lat);
        const longitude = parseFloat(lon);
        return !isNaN(latitude) && !isNaN(longitude) &&
               latitude >= -90 && latitude <= 90 &&
               longitude >= -180 && longitude <= 180;
      };

      // Check if we have valid coordinates
      if (destination.latitude && destination.longitude &&
          isValidCoordinate(destination.latitude, destination.longitude)) {
        // Navigate to map page to show location with marker
        this.$router.push({
          path: '/map',
          query: {
            lat: destination.latitude,
            lon: destination.longitude,
            name: destination.name,
            address: `${destination.city}, ${destination.state}`,
            showMarker: 'true'
          }
        });
      } else if (destination.coordinates &&
                 isValidCoordinate(destination.coordinates.lat || destination.coordinates.latitude,
                                 destination.coordinates.lon || destination.coordinates.longitude)) {
        // Alternative coordinate format
        const coords = destination.coordinates;
        this.$router.push({
          path: '/map',
          query: {
            lat: coords.lat || coords.latitude,
            lon: coords.lon || coords.longitude,
            name: destination.name,
            address: `${destination.city}, ${destination.state}`,
            showMarker: 'true'
          }
        });
      } else {
        // Fallback: use name and address for geocoding
        this.$router.push({
          path: '/map',
          query: {
            search: destination.name,
            address: `${destination.city}, ${destination.state}`,
            showMarker: 'true'
          }
        });
      }

      // Show success notification and close modal
      this.showNotification(`Showing ${destination.name} on map`, 'success');
      this.closeDestinationDetails();
    },

    closeDestinationDetails() {
      this.selectedDestination = null;

      // Reset mobile scroll state
      this.modalScrollY = 0;
      this.isContentSlideUp = false;

      // Remove scroll listener
      const scrollableContent = document.querySelector('.modal-scrollable-content');
      if (scrollableContent) {
        scrollableContent.removeEventListener('scroll', this.handleModalScroll);
      }

      // Re-enable scrolling on the body
      document.body.style.overflow = '';
    },

    nextImage() {
      if (!this.selectedDestination || !this.selectedDestination.images) return;
      this.imageLoading = true;
      this.currentImageIndex = (this.currentImageIndex + 1) % this.selectedDestination.images.length;
    },

    prevImage() {
      if (!this.selectedDestination || !this.selectedDestination.images) return;
      this.imageLoading = true;
      this.currentImageIndex = (this.currentImageIndex - 1 + this.selectedDestination.images.length) % this.selectedDestination.images.length;
    },

    scrollToTop() {
      // Scroll to the top of the page
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Switch to about tab
      this.activeTab = 'about';
    },

    // Handle clicks outside the suggestions dropdown - Optimized for performance
    handleClickOutside(event) {
      // Only process if suggestions are actually showing to avoid unnecessary work
      if (!this.showSuggestions) return;

      const searchContainer = document.querySelector('.search-container');
      if (searchContainer && !searchContainer.contains(event.target)) {
        this.showSuggestions = false;
      }
    },

    // Optimized touch handlers for smooth carousel - Performance enhanced
    handleTouchStart(event) {
      if (!this.selectedDestination?.images?.length || this.selectedDestination.images.length <= 1) return;

      this.touchStartX = event.touches[0].clientX;
      this.touchMoveX = 0;
      this.touchOffset = -this.currentImageIndex * 100;
      this.isDragging = true;
    },

    handleTouchMove(event) {
      if (!this.isDragging) return;

      // Only prevent default for horizontal movement to maintain scroll performance
      const currentX = event.touches[0].clientX;
      const deltaX = Math.abs(currentX - this.touchStartX);
      const deltaY = Math.abs(event.touches[0].clientY - (this.touchStartY || event.touches[0].clientY));

      // Only prevent default if horizontal movement is greater than vertical
      if (deltaX > deltaY) {
        event.preventDefault();
      }

      this.touchMoveX = currentX - this.touchStartX;

      // Use requestAnimationFrame for smooth updates
      if (!this.animationFrame) {
        this.animationFrame = requestAnimationFrame(() => {
          this.updateCarouselPosition();
          this.animationFrame = null;
        });
      }
    },

    updateCarouselPosition() {
      if (!this.isDragging) return;

      const containerWidth = window.innerWidth; // Use window width for better performance
      const movePercentage = (this.touchMoveX / containerWidth) * 100;

      // Simplified calculation for better performance
      const baseOffset = -this.currentImageIndex * 100;
      const newOffset = baseOffset + movePercentage;

      // Apply simple boundaries without resistance for better performance
      const maxImages = this.selectedDestination.images.length;
      const minOffset = -(maxImages - 1) * 100;
      const maxOffset = 0;

      this.touchOffset = Math.max(minOffset, Math.min(maxOffset, newOffset));
    },

    handleTouchEnd() {
      if (!this.isDragging) return;
      this.isDragging = false;

      // Cancel any pending animation frame
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }

      const threshold = window.innerWidth * 0.2; // Slightly higher threshold for better UX
      const diff = this.touchMoveX;

      // Simplified logic for better performance
      if (Math.abs(diff) > threshold) {
        if (diff > 0 && this.currentImageIndex > 0) {
          this.prevImage();
        } else if (diff < 0 && this.currentImageIndex < this.selectedDestination.images.length - 1) {
          this.nextImage();
        }
      }

      // Reset values
      this.touchMoveX = 0;
      this.touchOffset = -this.currentImageIndex * 100;
    },

    // Add back the scroll handler for slide-up effect
    handleModalScroll(event) {
      const scrollTop = event.target.scrollTop;
      const threshold = 50; // Pixels to scroll before sliding up

      // Only apply on mobile (screen width < 768px)
      if (window.innerWidth >= 768) return;

      this.modalScrollY = scrollTop;
      this.isContentSlideUp = scrollTop > threshold;
    },
  }
}
</script>

<style scoped>
/* Remove unnecessary mobile padding adjustments */
.min-h-screen {
  padding-bottom: 60px;
}

/* Basic styles for mobile layout */
@media (max-width: 768px) {
  .h-screen {
    height: 100vh;
    height: 100dvh;
  }
}

/* Ensure proper z-index for fixed elements */
.fixed {
  z-index: 50;
}

.md\:hidden.fixed {
  z-index: 9999;
}

/* Hide scrollbar but keep scrolling functionality */
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}

/* Basic text truncation */
.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Basic animations */
.animate-fadeIn {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideDown {
  from {
    transform: translate(-50%, -20px);
    opacity: 0;
  }
  to {
    transform: translate(-50%, 0);
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    transform: translate(-50%, 0);
    opacity: 1;
  }
  to {
    transform: translate(-50%, -20px);
    opacity: 0;
  }
}

/* Safe area for notched devices */
.top-safe-top {
  top: max(16px, env(safe-area-inset-top, 16px));
}

/* Simplified destination card styles */
.destination-card {
  transition: transform 0.2s ease;
  border: 1px solid transparent;
}

.destination-card:hover {
  transform: translateY(-2px);
}

.destination-card:active {
  transform: translateY(0);
}

/* Basic responsive styles */
@media (max-width: 640px) {
  .destination-card:active {
    transform: scale(0.98);
  }
}

/* Simplified image transitions */
.destination-card img {
  transition: opacity 0.3s ease;
}

/* Mobile text styles */
@media (max-width: 768px) {
  .destination-card h3 {
    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  .destination-card p {
    font-size: 0.75rem;
  }
}

/* Basic touch scrolling */
.touch-scroll {
  -webkit-overflow-scrolling: touch;
}

/* Remove unnecessary hardware acceleration and transforms */
.destination-card {
  will-change: transform;
}

/* Mobile modal styles with slide-up effect */
@media (max-width: 767px) {
  .slide-up-over-image {
    transform: translateY(-60px);
    border-radius: 20px 20px 0 0;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
    margin-top: -40px;
    position: relative;
    z-index: 40;
    transition: transform 0.3s ease-out;
  }

  .modal-scrollable-content {
    background: white;
    border-radius: 20px 20px 0 0;
    position: relative;
    z-index: 35;
  }

  /* Add back the scroll handler for slide-up effect */
  .modal-scrollable-content {
    transition: transform 0.3s ease-out;
  }
}

/* Desktop styles */
@media (min-width: 768px) {
  .slide-up-over-image {
    transform: none;
  }
}
</style>
